import { useRouter } from "next/router";
import { useState, useEffect } from "react";
import MainLayout from "../../components/MainLayout/MainLayout";
import { appServerSideProps } from "../../utils/getAppService";
import Head from "next/head";
import { leagueData } from "../../components/header/Header";
import UpdatePlan from "../../components/update-plan/UpdatePlan";
import Utils from "../../utils/Utils";
import WinningMetrics from "../../components/WinningMetrics";

const leagues = Object.keys(leagueData);

const Live = ({ user, title, serverLogs, query, queryProps }) => {
  const home_sq_score = queryProps.home_sq_score;
  const away_sq_score = queryProps.away_sq_score;
  const router = useRouter();
  const { gid, home, away, date } = router.query;
  const splitDate = date ? date.split("T")[0] : "";
  const [gameData, setGameData] = useState({
    home_team: home ? home : "",
    away_team: away ? away : "",
    formattedDate: splitDate ? splitDate : "",
  });
  const [freeGame, setFreeGame] = useState(false);
  const [isLive, setIsLive] = useState(false);
  const isStandardPlan = Utils.hasStandardPlan(user);
  const isPremiumPlan = Utils.hasPremiumPlan(user);
  const isPayed = isStandardPlan || isPremiumPlan;
  const [domLoaded, setDomLoaded] = useState(false);
  useEffect(() => {
    if (!home && !away && !date && gid) {
      handleClientRedirect(queryProps, gameData, router);
    }
    if (!gid) {
      router.push("/ncaa/shotquality-scores");
    }
  }, [gameData, gid]);

  useEffect(() => {
    handleServerDataFetch(gid, home, title, query, serverLogs, setGameData, router);
  }, [gid, home, title, query, serverLogs]);

  useEffect(() => {
    fetch(`${process.env.NEXT_PUBLIC_API_URL_API_URL}/api/college_mens/live-games`)
      .then((response) => response.json())
      .then((data) => {
        data = data.data;
        const freeLive = data.filter((game) => game.live_free === 1).map((game) => game.game_id + "");
        const liveGames = data.filter((game) => game.status === "live").map((game) => game.game_id + "");
        setFreeGame(freeLive.includes(gid));
        setIsLive(liveGames.includes(gid) || gid === "20036043412371");
      })
      .catch((err) => {
        if (err.message.includes("401")) {
          try {
            fetch(`${process.env.NEXT_PUBLIC_API_URL_API_URL}/api/refresh`, {
              method: "POST",
              headers: {
                Authorization: `Bearer ${authToken}`,
              },
              body: authToken,
            })
              .then((res) => res.json())
              .then((response) => {
                if (response.access_token) {
                  setCookie("token_access", response.access_token);
                  setAuthToken(response.access_token);
                } else {
                  console.error("Error refreshing token", response);
                }
              })
              .catch((error) => {
                console.error("Error during token refresh:", error);
              });
          } catch (error) {
            console.error("Error during token refresh:", error);
          }
        }
      });
    fetch(`${process.env.NEXT_PUBLIC_API_URL_API_URL}/api/nba/live-games`)
      .then((response) => response.json())
      .then((data) => {
        data = data.data;
        const freeLive = data.filter((game) => game.live_free === 1).map((game) => game.game_id + "");
        const liveGames = data.filter((game) => game.status === "live").map((game) => game.game_id + "");
        setFreeGame(freeLive.includes(gid));
        setIsLive(liveGames.includes(gid));
      })
      .catch((err) => {
        console.error("Error fetching live games:", err);
      });
    fetch(`${process.env.NEXT_PUBLIC_API_URL_API_URL}/api/wnba/live-games`)
      .then((response) => response.json())
      .then((data) => {
        data = data.data;
        const freeLive = data.filter((game) => game.live_free === 1).map((game) => game.game_id + "");
        const liveGames = data.filter((game) => game.status === "live").map((game) => game.game_id + "");
        setFreeGame(freeLive.includes(gid));
        setIsLive(liveGames.includes(gid));
      })
      .catch((err) => {
        console.error("Error fetching live games:", err);
      });
    setDomLoaded(true);
  }, []);
  return (
    <>
      <Head>
        {title ? (
          <title>{title}</title>
        ) : home ? (
          <title>
            {home} vs {away} | {splitDate} Live
          </title>
        ) : (
          <title>
            {gameData.home_team} vs {gameData.away_team} | {gameData.formattedDate} Live
          </title>
        )}
      </Head>
      <MainLayout>
        {domLoaded && (
          <>
            {!user && (
              <div className="mt-5 modal-overlay update-plan">
                <UpdatePlan />
              </div>
            )}
            {user && !isPayed && !freeGame ? (
              <div className="mt-5 modal-overlay update-plan">
                <UpdatePlan />
              </div>
            ) : user && isStandardPlan && !isPremiumPlan && !isLive && !freeGame ? (
              <div className="mt-5 modal-overlay update-plan">
                <UpdatePlan showPremium={true} />
              </div>
            ) : (
              <div className="content-container">
                {gid ? (
                  <iframe
                    className="live-iframe"
                    title="Live"
                    src={`https://embed.sq.video/?gid=${gid}${
                      home_sq_score ? "&hsq=" + home_sq_score + "&asq=" + away_sq_score : ""
                    }${
                      queryProps.status === "final"
                        ? "&hs=" + queryProps.home_score + "&as=" + queryProps.away_score
                        : ""
                    }`}
                    allowFullScreen
                  />
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "6rem",
                      fontSize: "1.25rem",
                      color: "white",
                    }}
                  >
                    GameId not found in the URL!
                  </div>
                )}
              </div>
            )}

            <WinningMetrics targetPage={"ScoreCenter"} />
          </>
        )}
      </MainLayout>
    </>
  );
};

export default Live;

export const getServerSideProps = appServerSideProps(async ({ appService, query }) => {
  const isUserLogged = appService.getUser();
  const queryProps = { ...query };
  let user = null;
  let gameData = null;
  if (isUserLogged) {
    user = await appService.get("api/account");
  }
  if (queryProps.gid) {
    gameData = await appService.get(`api/game/${queryProps.gid}`).catch((err) => {
      console.log(err, "err");
    });
    if (gameData && gameData.data) {
      const {
        home,
        away,
        date,
        home_sq_score = null,
        away_sq_score = null,
        home_score = null,
        away_score = null,
        status,
      } = gameData.data;
      queryProps.home = home;
      queryProps.away = away;
      queryProps.date = date;
      queryProps.home_sq_score = home_sq_score;
      queryProps.away_sq_score = away_sq_score;
      queryProps.home_score = home_score;
      queryProps.away_score = away_score;
      queryProps.status = status;
      // queryProps.data = gameData;

      const splitDate = date ? date.split("T")[0] : "";
      return {
        props: {
          user,
          serverLogs: `data: ${JSON.stringify(gameData)}`,
          title: `${home} vs ${away} | ${splitDate} Live`,
          queryProps,
          query,
        },
      };
    }
  }
  return { props: { user, queryProps, serverLogs: `data: ${JSON.stringify(gameData)}` } };
});

function handleClientRedirect(queryProps, gameData, router) {
  const { gid, home, away, date } = queryProps;
  if (home && away && date) {
    router.push(`/live?gid=${gid}&home=${home}&away=${away}&date=${date}`);
  } else if (gameData.home_team && gameData.away_team && gameData.formattedDate) {
    router.push(
      `/live?gid=${gid}&home=${gameData.home_team}&away=${gameData.away_team}&date=${gameData.formattedDate}`
    );
  }
}

async function handleServerDataFetch(gid, home, title, query, serverLogs, setGameData, router) {
  if (!home && !title && gid) {
    try {
      const response = await fetch(`https://embed.shotquality.com/data/betting?gid=${gid}`);
      const data = await response.json();

      if (data && data.game_info) {
        const { home_team, away_team, game_time } = data.game_info;
        const formattedDate = game_time?.split(" ")[0] || "";
        setGameData((prevGameData) => ({
          ...prevGameData,
          home_team,
          away_team,
          formattedDate,
        }));
      }
    } catch (error) {
      console.error("Error fetching game data:", error);
      console.log("wrong game id");
    }
  }
}
